import { useRoute } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useWorkflowVersions } from "@/hooks/useLocalStorage";
import { useEffect } from "react";
import { WorkflowCheckInOut } from "@/lib/api-services";
import { ArrowLeft } from "lucide-react";

export default function WorkflowVersion() {

  const [match, params] = useRoute("/workflowversion/:id");
  const wfId = Number(params?.id);
  const { data, isLoading, isError, refetch } = useWorkflowVersions(wfId);

  useEffect(() => {
    refetch();
  }, [wfId]);

  const formatDate = (date?: string | null) => {
    if (!date) return "";
    const d = new Date(date);
    return (
      d.toLocaleDateString() + "\n" + d.toLocaleTimeString()
    );
  };

  const handleView = (row: WorkflowCheckInOut) => {
    const workflowId = row.PMId || wfId;
    const versionRowId = row.Id;
    window.open(
      `https://workflow-designer.workfinity.com/workflow/${workflowId}?versionId=${versionRowId}`,
      "_blank"
    );
  };

  if (!match) return <div className="p-4">Invalid route</div>;
  if (isLoading) return <div className="p-4">Loading...</div>;
  if (isError) return <div className="p-4">Something went wrong</div>;

  return (
    <div className="p-4">
      <Card className="shadow-md rounded-2xl">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              Workflow Version
            </h2>

            <Button
              variant="outline"
              onClick={() => window.history.back()}
            >
              <ArrowLeft size={16} />
              Back
            </Button>
          </div>

          <div className="border bg-white border-gray-200 rounded-lg p-2 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>WF ID</TableHead>
                  <TableHead>Workflow Name</TableHead>
                  <TableHead>Check In Date</TableHead>
                  <TableHead>Check In By</TableHead>
                  <TableHead>Check In Comments</TableHead>
                  <TableHead>Check Out Date</TableHead>
                  <TableHead>Check Out By</TableHead>
                  <TableHead>Check Out Comments</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>View Workflow</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {data?.map((row: WorkflowCheckInOut, index: number) => (
                  <TableRow
                    key={row.Id ?? `${row.WFId}-${row.Version}-${index}`}
                    className={
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    }
                  >
                    <TableCell>{row.WFId}</TableCell>
                    <TableCell>{row.WorkflowName}</TableCell>

                    <TableCell className="whitespace-pre-line">
                      {formatDate(row.CheckInDate)}
                    </TableCell>

                    <TableCell>{row.CheckInBy || ""}</TableCell>

                    <TableCell className="max-w-xs truncate">
                      {row.CheckInComments || ""}
                    </TableCell>

                    <TableCell className="whitespace-pre-line">
                      {formatDate(row.CheckOutDate)}
                    </TableCell>

                    <TableCell>{row.CheckOutBy || ""}</TableCell>

                    <TableCell className="max-w-xs truncate">
                      {row.CheckOutComments || ""}
                    </TableCell>

                    <TableCell>{row.Version}</TableCell>

                    <TableCell>
                      <Button
                        variant="link"
                        onClick={() => handleView(row)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}